/**
 * South African bank universal branch codes, used to build the beneficiary
 * bank-code picker (BeneficiariesView).
 *
 * A "universal branch code" is a single 6-digit code a bank publishes for use
 * with any of its branches, which is exactly what the Beneficiary_Service
 * expects for `bankCode` (validated as exactly 6 numeric digits). Presenting a
 * fixed dropdown of real banks makes the submitted `bankCode` valid by
 * construction — the user physically cannot type a malformed code — while the
 * server validation stays authoritative (defence in depth).
 *
 * The codes below are the publicly published SA universal branch codes for the
 * listed banks. If a stored beneficiary carries a code that is not in this list
 * (e.g. a legacy or less common institution), the view adds it as an extra
 * option so an edit still round-trips.
 */

/** A selectable South African bank and its 6-digit universal branch code. */
export interface SaBank {
  /** Human-readable bank name shown in the option label. */
  readonly name: string;
  /** The 6-digit universal branch code submitted as `bankCode`. */
  readonly code: string;
}

/**
 * The picker list of South African banks, sorted by name. Each `code` is
 * exactly six digits so any selection satisfies the server's bank-code rule.
 */
export const SA_BANKS: readonly SaBank[] = [
  { name: 'Absa', code: '632005' },
  { name: 'African Bank', code: '430000' },
  { name: 'Bank Zero', code: '888000' },
  { name: 'Capitec', code: '470010' },
  { name: 'Discovery Bank', code: '679000' },
  { name: 'FNB (First National Bank)', code: '250655' },
  { name: 'Investec', code: '580105' },
  { name: 'Nedbank', code: '198765' },
  { name: 'Old Mutual', code: '462005' },
  { name: 'Standard Bank', code: '051001' },
  { name: 'TymeBank', code: '678910' },
];

/** The label shown for a bank in the picker: `Name (code)`. */
export function bankOptionLabel(bank: SaBank): string {
  return `${bank.name} (${bank.code})`;
}

/** Whether a bank code matches one of the known SA universal branch codes. */
export function isKnownBankCode(code: string): boolean {
  return SA_BANKS.some((bank) => bank.code === code);
}
